/**
 * Worker protocol (docs/SHEETS.md 4.1, ADR-0007).
 *
 * The main thread sends a *document*: LaTeX strings and the order to evaluate them
 * in. It never sends code, and the worker never sends code back. That is the whole
 * of the trust boundary on the client side, and it is worth keeping the message
 * types this boring so it stays obvious.
 *
 * Every request carries an `id` so a late reply from a superseded evaluation can be
 * dropped rather than painted over fresh results -- the classic reactive-editor bug
 * where fast typing shows you the answer to a question you already changed.
 */
import type { EvalError, MatrixQuantity, Quantity, ResultValue } from '../model/types'

/** One unit of work: a cell, already resolved to what it defines and reads. */
export interface EvalRequestCell {
  readonly cellId: string
  readonly kind: 'assign' | 'evaluate' | 'table' | 'plot'
  /** LaTeX for math and plot cells. */
  readonly latex?: string
  /** Canonical key this cell defines, for assignments. */
  readonly target?: string
  /** Literal assignments from a table cell. */
  readonly rows?: readonly { symbol: string; value: number; unit: string }[]
  /** Plot sweep. */
  readonly sweep?: { symbol: string; from: number; to: number; steps: number }
}

export type WorkerRequest =
  | { readonly type: 'boot' }
  | {
      readonly type: 'evaluate'
      readonly id: number
      /** Cells to run, already in document order. */
      readonly cells: readonly EvalRequestCell[]
      /**
       * Symbols already known from cells that did not change, so an incremental
       * pass need not re-run the whole sheet.
       */
      readonly carried: Readonly<Record<string, ResultValue>>
    }
  | { readonly type: 'reset' }

export interface CellOutcome {
  readonly cellId: string
  readonly ok: boolean
  readonly value?: Quantity
  readonly matrix?: MatrixQuantity
  readonly series?: readonly (readonly [number, number])[]
  readonly error?: EvalError
}

export type WorkerResponse =
  | { readonly type: 'booted'; readonly engineVersion: string }
  | { readonly type: 'boot-failed'; readonly messageKey: string }
  | {
      readonly type: 'result'
      readonly id: number
      readonly outcomes: readonly CellOutcome[]
      /** Symbol table after the pass, for carrying into the next one. */
      readonly scope: Readonly<Record<string, ResultValue>>
    }
  | { readonly type: 'failed'; readonly id: number; readonly messageKey: string }

/** Narrowing helpers, so message handlers never index into an unchecked shape. */
export function isWorkerResponse(value: unknown): value is WorkerResponse {
  if (typeof value !== 'object' || value === null) return false
  const type = (value as { type?: unknown }).type
  return type === 'booted' || type === 'boot-failed' || type === 'result' || type === 'failed'
}
